import { AxiosError } from "axios";
import { BigNumber, constants, ethers } from "ethers";
import React, { useCallback } from "react";
import { SWRResponse } from "swr";
import {
  GenericErrorResponse,
  GetOrders200Response,
  InstrumentTypeResponse,
  OrderTypeResponse,
  PostOrdersPayload,
  SideResponse,
} from "../../codegen-api";
import { AuthContext } from "../AuthContext";
import { getUsdcDecimals } from "../../config/chains";
import { ICreateOrderBody } from "../../interfaces/Order";
import { authApi } from "../../services/api/apiFetcher";
import { getDomainData } from "../../utils/signing";
import { supportedChainId } from "../../utils/wallet/connectors";
import { useSFX } from "../../hooks/useSFX";
import useWallet from "../../hooks/wallet/useWallet";
import { useClock } from "../../hooks/api/clock/useClock";
import { AssetResponse } from "../../utils/asset";
import { toTokenUnits } from "../../utils/decimalString";
import { unwrapApiError } from "../../utils/errors";
import { orderType } from "./helpers";
import { CancelAllOrdersResponse, IOrderMessage } from "./types";

interface UseOrderActionsParams {
  signingKey: React.ContextType<typeof AuthContext>["signingKey"];
  account: React.ContextType<typeof AuthContext>["account"];
  getTimestamp: ReturnType<typeof useClock>["getTimestamp"];
  fetcher: ReturnType<typeof authApi>;
  playSound: ReturnType<typeof useSFX>["playSound"];
  revalidateOrdersSoon: (includeImmediate?: boolean) => void;
  deleteAuthInfo: React.ContextType<typeof AuthContext>["deleteAuthInfo"];
  swr: SWRResponse<GetOrders200Response[], AxiosError>;
}

export function useOrderActions({
  signingKey,
  account,
  getTimestamp,
  fetcher,
  playSound,
  revalidateOrdersSoon,
  deleteAuthInfo,
  swr,
}: UseOrderActionsParams) {
  // Handle errors that mean the stored signing key can never produce a valid
  // order again. ORDER_INVALID_SIGNATURE belongs here too: a key registered
  // under a different chain's EIP-712 domain fails every submission, and
  // without clearing it the user retries forever with the same bad key.
  const { isWrongNetwork } = useWallet();

  const handleInvalidSigningKeyError = useCallback(
    (error?: any) => {
      const code = unwrapApiError(error);
      if (
        code === "ORDER_INVALID_SIGNING_KEY" ||
        code === "ORDER_INVALID_SIGNATURE"
      ) {
        // Delete signing key and resign
        deleteAuthInfo("signing");
      }
    },
    [deleteAuthInfo]
  );

  // Util function for creating an order request
  const getCreateOrderRequest = useCallback(
    async (data: ICreateOrderBody) => {
      if (!signingKey || !account) {
        return undefined;
      }

      // The order is signed under `supportedChainId` (the build-time chain).
      // Submitting while the wallet sits on another network produces a
      // signature the backend cannot verify, surfacing much later as a generic
      // "invalid signature". Refuse here, where the cause is still obvious.
      if (isWrongNetwork) {
        throw Error(
          "Wallet is connected to the wrong network. Switch to the network " +
            "this app is configured for before placing an order."
        );
      }

      // Get signing keys, and sign message
      const salt = Math.floor(Math.random() * 100000);
      let limitPrice;
      // `toTokenUnits`, not `parseUnits`: a price that reached here as exponent
      // text ("5e-7") made ethers throw an opaque "invalid decimal value".
      const triggerPrice = data.trigger
        ? toTokenUnits(data.trigger, 18)
        : undefined;
      if (triggerPrice === null) {
        throw Error(`Invalid trigger price: ${data.trigger}`);
      }
      if (data.orderType === OrderTypeResponse.Limit) {
        // Prices are in 18 decimals (wei) for the smart contracts
        limitPrice = toTokenUnits(data.price, 18);
        if (!limitPrice) {
          throw Error(`Invalid limit price: ${data.price}`);
        }
      } else {
        limitPrice =
          data.side === SideResponse.Buy
            ? constants.MaxUint256
            : BigNumber.from(0);
      }

      // Scale amount to token decimals (e.g., 18 for DogeOS USDC, 6 for standard USDC)
      // Amount represents USD value for market orders, contract size for limit orders
      // FLOORED to the token's decimals: `toFixed` rounded half-up, so
      // 0.0000015 was sent as 0.000002 and 5e-7 as 0.
      const usdcDecimals = getUsdcDecimals();
      const amount = toTokenUnits(data.amount, usdcDecimals);
      if (!amount) {
        throw Error(`Invalid order amount: ${data.amount}`);
      }
      const timestamp = getTimestamp();

      const orderMessage: IOrderMessage = {
        maker: account,
        isBuy: data.side === SideResponse.Buy,
        instrument: String(data.instrument),
        limitPrice: limitPrice.toString(),
        amount: amount.toString(),
        stop: data.stop,
        trigger: triggerPrice?.toString(),
        salt: String(salt),
        reduceOnly: data.reduceOnly,
        mmp: data.mmp,
        timestamp,
      };

      const signer = new ethers.Wallet(signingKey);
      const domainData = getDomainData(supportedChainId);

      /* eslint no-underscore-dangle: 0 */
      const orderSignature = await signer._signTypedData(
        domainData,
        { Order: orderType },
        orderMessage
      );

      const request: PostOrdersPayload = {
        maker: orderMessage.maker,
        is_buy: orderMessage.isBuy,
        instrument_id: orderMessage.instrument, // Keep as string to avoid scientific notation
        limit_price: String(orderMessage.limitPrice),
        amount: String(orderMessage.amount),
        salt: String(orderMessage.salt),
        stop: orderMessage.stop,
        trigger: orderMessage.trigger,
        signature: orderSignature,
        system_type: "WEB",
        reduce_only: orderMessage.reduceOnly,
        mmp: orderMessage.mmp,
        timestamp: String(timestamp),
        close_position: data.closePosition,
        order_type: data.orderType, // Add order type so backend knows if it's MARKET or LIMIT
      };
      return request;
    },
    [signingKey, account, getTimestamp, isWrongNetwork]
  );

  const createOrder = useCallback(
    async (data: ICreateOrderBody) => {
      const createOrderRequest: PostOrdersPayload | undefined =
        await getCreateOrderRequest(data);
      if (!createOrderRequest) {
        throw Error("Unauthenticated");
      }

      // Submit order
      try {
        const response = (
          await (
            await fetcher.postOrders(createOrderRequest)
          )()
        ).data;

        // If valid orderId, refresh orders
        if (response.order_id) {
          playSound("order_placed", true);
          revalidateOrdersSoon();
        }
        return response;
      } catch (error) {
        handleInvalidSigningKeyError(error);

        const genericResponseAxiosError =
          error as AxiosError<GenericErrorResponse>;
        throw Error(
          genericResponseAxiosError.response?.data?.error ||
            "Error Creating Order"
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getCreateOrderRequest, fetcher, playSound, revalidateOrdersSoon]
  );

  const removeOrder = useCallback(
    async (order_id: string) => {
      const response = (await (await fetcher.deleteOrdersOrderId(order_id))())
        .data;

      // If success, mutate the current data and filter out the removed order id
      if (swr.data && response.order_id) {
        swr.mutate(
          swr.data.filter((o) => o.order_id !== order_id),
          { revalidate: false }
        );
      }
      // Reconcile whenever the cancel succeeded — deliberately OUTSIDE the
      // guard above, which only decides whether a local optimistic patch is
      // possible. The local filter is a guess written with `revalidate: false`,
      // so without this the cancel was never checked against the backend: a
      // rejected cancel, or a fill that raced it, stayed invisible until the
      // 30s `/orders` poll. Gating the refresh on `swr.data` would reintroduce
      // the same hole whenever the list has not loaded yet.
      if (response.order_id) {
        revalidateOrdersSoon();
      }
      return response;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetcher, revalidateOrdersSoon, swr.data, swr.mutate]
  );

  // This is an MARKET_MAKER specific API
  const removeAllOrders = useCallback(
    async (type?: InstrumentTypeResponse, asset?: AssetResponse) => {
      try {
        const response = (
          await (
            await fetcher.deleteOrdersAll({ instrument_type: type, asset })
          )()
        ).data as CancelAllOrdersResponse;

        const cancelledOrderIds = (
          response.cancelled?.length ? response.cancelled : response.order_ids || []
        ).map(String);

        if (swr.data && cancelledOrderIds.length > 0) {
          swr.mutate(
            swr.data?.filter(
              (order) => !cancelledOrderIds.includes(order.order_id)
            ),
            { revalidate: false }
          );
        }

        if (cancelledOrderIds.length > 0 || response.failed?.length) {
          revalidateOrdersSoon();
        }

        return response;
      } catch (error) {
        const genericResponseAxiosError =
          error as AxiosError<GenericErrorResponse>;
        throw Error(
          genericResponseAxiosError.response?.data?.error ||
            "Error Cancelling All Orders"
        );
      }
    },
    [fetcher, revalidateOrdersSoon, swr]
  );

  const editOrder = useCallback(
    async (orderId: string, newOrder: ICreateOrderBody) => {
      const createOrderRequest = await getCreateOrderRequest(newOrder);

      if (!createOrderRequest) {
        throw Error("Unauthenticated");
      }

      // Submit order
      try {
        const response = (
          await (
            await fetcher.postOrdersOrderId(orderId, createOrderRequest)
          )()
        ).data;

        if (swr.data) {
          const newOrders = swr.data.map((order) => {
            if (order.order_id === orderId) {
              return {
                ...order,
                order_id: response.order_id,
                price: newOrder.price || order.price,
                amount: newOrder.amount || order.amount,
                trigger: newOrder.trigger || order.trigger,
                filled: "0",
              };
            }
            return order;
          });
          swr.mutate(newOrders, { revalidate: false });
        }
        // Same gap as `removeOrder`, and likewise outside the cache guard: the
        // remap above is a local guess (it even resets `filled` to "0") written
        // with `revalidate: false`. An edit the backend amended, rejected, or
        // partially filled stayed wrong until the 30s poll.
        if (response.order_id) {
          revalidateOrdersSoon();
        }
        return response;
      } catch (error) {
        handleInvalidSigningKeyError(error);

        const genericResponseAxiosError =
          error as AxiosError<GenericErrorResponse>;
        throw Error(
          genericResponseAxiosError.response?.data?.error ||
            "Error Editing Order"
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetcher, getCreateOrderRequest, revalidateOrdersSoon, swr.data, swr.mutate]
  );

  return {
    handleInvalidSigningKeyError,
    getCreateOrderRequest,
    createOrder,
    removeOrder,
    removeAllOrders,
    editOrder,
  };
}
